import React, {
  useMemo,
  useCallback,
  useEffect,
  useState,
  useContext,
} from "react";
import { useTheme } from "@mui/material/styles";
import {
  Avatar,
  Box,
  Chip,
  IconButton,
  Divider,
  Stack,
  Typography,
  Tooltip,
} from "@mui/material";
import { useDispatch, useSelector } from "store";
import { openDrawer } from "store/slices/menu";
import { IconMenu2 } from "@tabler/icons";
import AutorenewIcon from "@mui/icons-material/Autorenew";
import {
  AccountBalanceWalletRounded as WalletIcon,
  PeopleAltRounded as VisitorsIcon,
  RequestQuoteRounded as WithdrawIcon,
  PhoneAndroidRounded as PhoneIcon,
} from "@mui/icons-material";
//import io from "socket.io-client";

// Project Imports
import LogoSection from "../LogoSection";
import MobileSection from "./MobileSection";
import ProfileSection from "./ProfileSection";
//import MegaMenuSection from "./MegaMenuSection";
//import NotificationSection from "./NotificationSection";
import {
  getTMNData,
  getSMSBalance,
  getAgentBalance,
  getBibbyxBalance,
  getOnpayMerchantBalance,
} from "store/slices/user";

import scbLogo from "../../../assets/images/pages/scb-logo.png";
import tmnLogo from "../../../assets/images/pages/tmn-mini-logo.webp";
// Initialize Socket

import { SocketContext } from "../../../SocketContext";

// ==============================|| MAIN NAVBAR / HEADER ||============================== //

const Header = () => {
  const socket = useContext(SocketContext);
  const theme = useTheme();
  const dispatch = useDispatch();
  const [onlineUsers, setOnlineUsers] = useState();
  // Redux Selectors
  const { drawerOpen } = useSelector((state) => state.menu);
  const {
    withdrawrequestlist,
    //agentBalance,
    tmnData,
    SMSBalance,
    agentBalance,
    bibbyxBalance,
    onpayMerchantBalance,
  } = useSelector((state) => state.user);

  const onpayBalances = useMemo(() => {
    const items = Array.isArray(onpayMerchantBalance?.data)
      ? onpayMerchantBalance.data
      : [];
    let withdraw = 0;
    let deposit = 0;
    for (const item of items) {
      if (!item || typeof item !== "object") continue;
      const t = String(item.type || "").toLowerCase();
      const b = Number(item.balance) || 0;
      if (t === "withdraw") withdraw = b;
      if (t === "deposit") deposit = b;
    }
    return { withdraw, deposit };
  }, [onpayMerchantBalance]);

  // Memoized Data
  const pendingWithdrawals = useMemo(() => {
    return withdrawrequestlist;
  }, [withdrawrequestlist]);

  // Handlers
  const handleClickRefresh = useCallback(() => {
    //dispatch(getAgentBalance());
    dispatch(getSMSBalance());
    dispatch(getTMNData());
    dispatch(getAgentBalance());
    dispatch(getBibbyxBalance());
    dispatch(getOnpayMerchantBalance());
  }, [dispatch]);

  useEffect(() => {
    handleClickRefresh(); // Call the function once on the first render
  }, [handleClickRefresh]);

  const handleDrawerToggle = useCallback(() => {
    dispatch(openDrawer(!drawerOpen));
  }, [dispatch, drawerOpen]);

  useEffect(() => {
    // ฟังก์ชันสำหรับดึงข้อมูลผู้ใช้ออนไลน์จากเซิร์ฟเวอร์
    const fetchOnlineUsers = () => {
      socket.emit("get-online-users"); // ส่งคำขอไปที่เซิร์ฟเวอร์
    };

    // เรียกใช้งานฟังก์ชันเมื่อ component โหลดครั้งแรก
    fetchOnlineUsers();

    // ตั้งค่า interval ให้ดึงข้อมูลทุกๆ 10 นาที
    const interval = setInterval(() => {
      fetchOnlineUsers();
    }, 600000); // 10 นาที = 600,000 milliseconds

    // ฟัง event 'online-users' ที่ถูกส่งมาจากเซิร์ฟเวอร์
    socket.on("online-users", (data) => {
      setOnlineUsers(data.count);
    });

    // Cleanup interval และ event listener เมื่อ component ถูกทำลาย
    return () => {
      clearInterval(interval); // ลบ interval เมื่อ component ถูกทำลาย
      socket.off("online-users"); // ปิดการฟัง event
    };
  }, [socket]);

  // Fetch Initial Data
  /*useEffect(() => {
    dispatch(getAgentBalance());
    dispatch(getGSBData());
    dispatch(getTMNData());
  }, [dispatch]);

  // Socket Events
  useEffect(() => {
    socket.emit("get-client-count", "get");

    const withdrawListener = () => {
      setTimeout(() => {
        dispatch(getWithdrawHistory());
      }, 1000);
    };

    socket.on("withdraw-request-listen", withdrawListener);
    console.log("Header Withdraw Listen");
    return () => {
      socket.off("withdraw-request-listen", withdrawListener);
    };
  }, [dispatch]);*/

  return (
    <>
      {/* Logo & Toggler Button */}
      <Box
        sx={{
          width: 228,
          display: "flex",
          [theme.breakpoints.down("md")]: {
            width: "auto",
          },
        }}
      >
        <Box
          component="span"
          sx={{ display: { xs: "none", md: "block" }, flexGrow: 1 }}
        >
          <LogoSection />
        </Box>
        <Avatar
          variant="rounded"
          sx={{
            background: "rgba(255, 255, 255, 0.15)",
            backdropFilter: "blur(10px)",
            border: "1px solid rgba(255, 255, 255, 0.2)",
            transition: "all 0.3s ease",
            cursor: "pointer",
            color: "#fff",
            "&:hover": {
              background: "rgba(255, 255, 255, 0.25)",
              transform: "scale(1.05)",
            },
          }}
          onClick={handleDrawerToggle}
        >
          <IconMenu2 stroke={1.5} size="20px" />
        </Avatar>
      </Box>
      {/* Spacer */}
      <Box sx={{ flexGrow: 1 }} />
      {/* Refresh Button */}
      <Tooltip title="รีเฟรชข้อมูล" arrow>
        <IconButton
          aria-label="refresh"
          onClick={handleClickRefresh}
          sx={{
            background: "rgba(255, 255, 255, 0.15)",
            backdropFilter: "blur(10px)",
            border: "1px solid rgba(255, 255, 255, 0.2)",
            color: "#fff",
            mr: 2,
            transition: "all 0.3s ease",
            "&:hover": {
              background: "rgba(255, 255, 255, 0.25)",
              transform: "rotate(180deg)",
            },
          }}
        >
          <AutorenewIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      {/* Stats Cards Container */}
      <Stack
        direction="row"
        spacing={1.5}
        sx={{ display: { xs: "none", lg: "flex" } }}
        alignItems="center"
      >
        {/* Bibbyx Balance */}
        <Box
          sx={{
            background: "rgba(255, 255, 255, 0.15)",
            backdropFilter: "blur(10px)",
            borderRadius: "12px",
            border: "1px solid rgba(255, 255, 255, 0.2)",
            px: 2,
            py: 1,
            display: "flex",
            alignItems: "center",
            gap: 1,
            transition: "all 0.3s ease",
            "&:hover": {
              background: "rgba(255, 255, 255, 0.2)",
              transform: "translateY(-2px)",
            },
          }}
        >
          <PhoneIcon sx={{ color: "#fff", fontSize: 20 }} />
          <Stack spacing={0}>
            <Typography
              variant="caption"
              sx={{
                color: "rgba(255, 255, 255, 0.8)",
                fontSize: "0.7rem",
                lineHeight: 1,
              }}
            >
              Bibbyx
            </Typography>
            <Typography
              variant="body2"
              sx={{ color: "#fff", fontWeight: 700, fontSize: "0.875rem" }}
            >
              {parseFloat(bibbyxBalance?.data?.balance || 0).toLocaleString()}
            </Typography>
          </Stack>
          <Chip
            size="small"
            sx={{
              height: 6,
              width: 6,
              minWidth: 6,
              borderRadius: "50%",
              background:
                parseFloat(bibbyxBalance?.data?.balance || 0) > 100
                  ? "#10b981"
                  : "#f59e0b",
              "& .MuiChip-label": { display: "none" },
            }}
          />
        </Box>

        {/* Onpay Merchant Balance */}
        <Box
          sx={{
            background: "rgba(255, 255, 255, 0.15)",
            backdropFilter: "blur(10px)",
            borderRadius: "12px",
            border: "1px solid rgba(255, 255, 255, 0.2)",
            px: 2,
            py: 1,
            display: "flex",
            alignItems: "center",
            gap: 1,
            transition: "all 0.3s ease",
            "&:hover": {
              background: "rgba(255, 255, 255, 0.2)",
              transform: "translateY(-2px)",
            },
          }}
        >
          <WalletIcon sx={{ color: "#fff", fontSize: 20 }} />
          <Stack spacing={0}>
            <Typography
              variant="caption"
              sx={{
                color: "rgba(255, 255, 255, 0.8)",
                fontSize: "0.7rem",
                lineHeight: 1,
              }}
            >
              Onpay
            </Typography>
            <Typography
              variant="body2"
              sx={{ color: "#fff", fontWeight: 700, fontSize: "0.875rem" }}
            >
              W: {Number(onpayBalances.withdraw || 0).toLocaleString()} | D:{" "}
              {Number(onpayBalances.deposit || 0).toLocaleString()}
            </Typography>
          </Stack>
        </Box>

        {/* TMN Balance */}
        <Box
          sx={{
            background: "rgba(255, 255, 255, 0.15)",
            backdropFilter: "blur(10px)",
            borderRadius: "12px",
            border: "1px solid rgba(255, 255, 255, 0.2)",
            px: 2,
            py: 1,
            display: "flex",
            alignItems: "center",
            gap: 1,
            transition: "all 0.3s ease",
            "&:hover": {
              background: "rgba(255, 255, 255, 0.2)",
              transform: "translateY(-2px)",
            },
          }}
        >
          <img
            src={tmnLogo}
            width="24"
            height="24"
            alt="tmn"
            style={{ borderRadius: "4px" }}
          />
          <Stack spacing={0}>
            <Typography
              variant="caption"
              sx={{
                color: "rgba(255, 255, 255, 0.8)",
                fontSize: "0.7rem",
                lineHeight: 1,
              }}
            >
              TrueMoney
            </Typography>
            <Typography
              variant="body2"
              sx={{ color: "#fff", fontWeight: 700, fontSize: "0.875rem" }}
            >
              {parseFloat(tmnData?.balance || 0).toLocaleString()}
            </Typography>
          </Stack>
          <Chip
            size="small"
            sx={{
              height: 6,
              width: 6,
              minWidth: 6,
              borderRadius: "50%",
              background: tmnData?.balance > 20 ? "#10b981" : "#ef4444",
              "& .MuiChip-label": { display: "none" },
            }}
          />
        </Box>

        {/* SMS Balance (SCB) */}
        <Box
          sx={{
            background: "rgba(255, 255, 255, 0.15)",
            backdropFilter: "blur(10px)",
            borderRadius: "12px",
            border: "1px solid rgba(255, 255, 255, 0.2)",
            px: 2,
            py: 1,
            display: "flex",
            alignItems: "center",
            gap: 1,
            transition: "all 0.3s ease",
            "&:hover": {
              background: "rgba(255, 255, 255, 0.2)",
              transform: "translateY(-2px)",
            },
          }}
        >
          <img
            src={scbLogo}
            width="24"
            height="24"
            alt="scb"
            style={{ borderRadius: "4px" }}
          />
          <Stack spacing={0}>
            <Typography
              variant="caption"
              sx={{
                color: "rgba(255, 255, 255, 0.8)",
                fontSize: "0.7rem",
                lineHeight: 1,
              }}
            >
              SCB (SMS)
            </Typography>
            <Typography
              variant="body2"
              sx={{ color: "#fff", fontWeight: 700, fontSize: "0.875rem" }}
            >
              {parseFloat(SMSBalance || 0).toLocaleString()}
            </Typography>
          </Stack>
          <Chip
            size="small"
            sx={{
              height: 6,
              width: 6,
              minWidth: 6,
              borderRadius: "50%",
              background: SMSBalance > 20 ? "#10b981" : "#ef4444",
              "& .MuiChip-label": { display: "none" },
            }}
          />
        </Box>

        {/* Agent Credit */}
        <Box
          sx={{
            background: "rgba(255, 255, 255, 0.15)",
            backdropFilter: "blur(10px)",
            borderRadius: "12px",
            border: "1px solid rgba(255, 255, 255, 0.2)",
            px: 2,
            py: 1,
            display: "flex",
            alignItems: "center",
            gap: 1,
            transition: "all 0.3s ease",
            "&:hover": {
              background: "rgba(255, 255, 255, 0.2)",
              transform: "translateY(-2px)",
            },
          }}
        >
          <WalletIcon sx={{ color: "#fff", fontSize: 20 }} />
          <Stack spacing={0}>
            <Typography
              variant="caption"
              sx={{
                color: "rgba(255, 255, 255, 0.8)",
                fontSize: "0.7rem",
                lineHeight: 1,
              }}
            >
              เครดิต
            </Typography>
            <Typography
              variant="body2"
              sx={{ color: "#fff", fontWeight: 700, fontSize: "0.875rem" }}
            >
              {parseFloat(agentBalance || 0).toLocaleString()}
            </Typography>
          </Stack>
          <Chip
            size="small"
            sx={{
              height: 6,
              width: 6,
              minWidth: 6,
              borderRadius: "50%",
              background: agentBalance > 1000 ? "#10b981" : "#ef4444",
              "& .MuiChip-label": { display: "none" },
            }}
          />
        </Box>

        {/* Online Users */}
        <Box
          sx={{
            background: "rgba(255, 255, 255, 0.15)",
            backdropFilter: "blur(10px)",
            borderRadius: "12px",
            border: "1px solid rgba(255, 255, 255, 0.2)",
            px: 2,
            py: 1,
            display: "flex",
            alignItems: "center",
            gap: 1,
            transition: "all 0.3s ease",
            "&:hover": {
              background: "rgba(255, 255, 255, 0.2)",
              transform: "translateY(-2px)",
            },
          }}
        >
          <VisitorsIcon sx={{ color: "#fff", fontSize: 20 }} />
          <Stack spacing={0}>
            <Typography
              variant="caption"
              sx={{
                color: "rgba(255, 255, 255, 0.8)",
                fontSize: "0.7rem",
                lineHeight: 1,
              }}
            >
              เข้าชม
            </Typography>
            <Typography
              variant="body2"
              sx={{ color: "#fff", fontWeight: 700, fontSize: "0.875rem" }}
            >
              {onlineUsers ?? "-"}
            </Typography>
          </Stack>
        </Box>

        {/* Pending Withdrawals */}
        <Box
          sx={{
            background:
              pendingWithdrawals.length > 0
                ? "rgba(239, 68, 68, 0.2)"
                : "rgba(255, 255, 255, 0.15)",
            backdropFilter: "blur(10px)",
            borderRadius: "12px",
            border:
              pendingWithdrawals.length > 0
                ? "1px solid rgba(239, 68, 68, 0.4)"
                : "1px solid rgba(255, 255, 255, 0.2)",
            px: 2,
            py: 1,
            display: "flex",
            alignItems: "center",
            gap: 1,
            transition: "all 0.3s ease",
            animation:
              pendingWithdrawals.length > 0
                ? "pulse 2s ease-in-out infinite"
                : "none",
            "&:hover": {
              background:
                pendingWithdrawals.length > 0
                  ? "rgba(239, 68, 68, 0.3)"
                  : "rgba(255, 255, 255, 0.2)",
              transform: "translateY(-2px)",
            },
            "@keyframes pulse": {
              "0%, 100%": {
                boxShadow: "0 0 0 0 rgba(239, 68, 68, 0.4)",
              },
              "50%": {
                boxShadow: "0 0 0 8px rgba(239, 68, 68, 0)",
              },
            },
          }}
        >
          <WithdrawIcon sx={{ color: "#fff", fontSize: 20 }} />
          <Stack spacing={0}>
            <Typography
              variant="caption"
              sx={{
                color: "rgba(255, 255, 255, 0.8)",
                fontSize: "0.7rem",
                lineHeight: 1,
              }}
            >
              คำขอถอน
            </Typography>
            <Typography
              variant="body2"
              sx={{ color: "#fff", fontWeight: 700, fontSize: "0.875rem" }}
            >
              {pendingWithdrawals.length}
            </Typography>
          </Stack>
          {pendingWithdrawals.length > 0 && (
            <Chip
              size="small"
              sx={{
                height: 6,
                width: 6,
                minWidth: 6,
                borderRadius: "50%",
                background: "#ef4444",
                animation: "blink 1s ease-in-out infinite",
                "& .MuiChip-label": { display: "none" },
                "@keyframes blink": {
                  "0%, 100%": { opacity: 1 },
                  "50%": { opacity: 0.3 },
                },
              }}
            />
          )}
        </Box>
      </Stack>
      {/* Mobile View - Compact Stats */}
      <Stack
        direction="row"
        spacing={1}
        sx={{ display: { xs: "flex", lg: "none" }, mr: 1 }}
        alignItems="center"
      >
        <Chip
          label={`${pendingWithdrawals.length} ถอน`}
          size="small"
          sx={{
            background:
              pendingWithdrawals.length > 0
                ? "rgba(239, 68, 68, 0.9)"
                : "rgba(255, 255, 255, 0.15)",
            color: "#fff",
            fontWeight: 600,
            fontSize: "0.75rem",
          }}
        />
      </Stack>
      {/* Divider */}
      <Divider
        orientation="vertical"
        flexItem
        sx={{
          mx: 2,
          borderColor: "rgba(255, 255, 255, 0.2)",
          display: { xs: "none", sm: "block" },
        }}
      />
      {/* Notification & Profile */}
      <ProfileSection />
      {/* Mobile Header */}
      <Box sx={{ display: { xs: "block", sm: "none" } }}>
        <MobileSection />
      </Box>
    </>
  );
};

export default Header;
